import { useEffect, useState } from "react";
import { Layers, Database, TrendingUp, Loader2 } from "lucide-react";
import { axiosInstance } from "../../lib/axios";

// ── My Bids, in one row ──────────────────────────────────────────────────────
//
// Three sections — Bids, Connects, Conversion — in a single container with
// vertical dividers. The separate Win Rate panel is gone; Bid → Win is one of
// the three conversion rings.
//
// Every figure comes from the server (lib/bidMetrics.js) rather than being
// recomputed here, so this panel, the bid list and the Reports module cannot
// end up disagreeing about what "won" means.
//
// The rule that shapes most of the code below: a rate with no denominator is
// UNKNOWN, and is shown as a dash. Printing 0% where nothing has closed yet
// states something about performance that the data does not support.

export interface BidStats {
  bids: {
    total: number;
    value: number;
    won: Bucket;
    negotiation: Bucket;
    lost: Bucket;
    directlyAwarded: Bucket;
    voided: Bucket;
    open: Bucket;
  };
  connects: {
    used: number;
    allowance: number | null;
    usedPct: number | null;
    remaining: number | null;
    cost: number;
    boosted: number;
    organic: number;
    classified: number;
    boostedPct: number | null;
    organicPct: number | null;
  };
  conversion: Record<string, Conversion>;
  excluded: { voided: number; directlyAwarded: number; eligibleTotal: number };
}

interface Bucket { count: number; value: number }
interface Conversion {
  rate: number | null;
  numerator: number;
  denominator: number;
  unavailableReason?: string;
}

const money = (n: number) => `$${Number(n || 0).toLocaleString()}`;
const num = (n: number) => Number(n || 0).toLocaleString();

const STATUS_ROWS = [
  { key: "won", label: "Won", dot: "bg-emerald-500", status: "Successfully Converted" },
  { key: "negotiation", label: "Negotiation", dot: "bg-amber-500", status: "In Negotiation" },
  { key: "lost", label: "Lost", dot: "bg-rose-500", status: "Hired Other Freelancer" },
  { key: "directlyAwarded", label: "Directly Awarded", dot: "bg-slate-400", status: "Awarded Directly" },
] as const;

const RINGS = [
  {
    key: "bidToWin",
    label: "Bid → Win",
    colour: "#10b981",
    tip: "Bids won, out of the bids where an outcome was possible. Deleted jobs and directly-awarded work are left out — no bid was placed, or no outcome could happen.",
  },
  {
    key: "bidToNegotiation",
    label: "Bid → Negotiation",
    colour: "#f59e0b",
    tip: "Bids that reached negotiation, out of the same eligible total.",
  },
  {
    key: "negotiationToWin",
    label: "Negotiation → Win",
    colour: "#8b5cf6",
    tip: "Of the bids that reached negotiation, how many were won.",
  },
] as const;

const Ring = ({ pct, colour }: { pct: number | null; colour: string }) => {
  const r = 22;
  const c = 2 * Math.PI * r;
  const filled = pct === null ? 0 : (Math.min(100, Math.max(0, pct)) / 100) * c;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
      <circle cx="28" cy="28" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
      {pct !== null && (
        <circle
          cx="28" cy="28" r={r} fill="none" stroke={colour} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={`${filled} ${c}`} transform="rotate(-90 28 28)"
        />
      )}
      <text x="28" y="32" textAnchor="middle" className="fill-slate-800 font-bold" style={{ fontSize: pct === null ? 16 : 13 }}>
        {pct === null ? "—" : `${pct}%`}
      </text>
    </svg>
  );
};

const BidStatsPanel = ({ onOpenStatus }: { onOpenStatus?: (status: string) => void }) => {
  const [stats, setStats] = useState<BidStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    axiosInstance
      .get("/bids/stats")
      .then(({ data }) => live && setStats(data))
      .catch((e) => live && setError(e?.response?.data?.message || "Could not load the statistics."))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center">
        <Loader2 size={18} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (error || !stats) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500">
        {error || "No statistics available."}
      </div>
    );
  }

  const { bids, connects, conversion } = stats;

  return (
    // Horizontal scroll rather than stacking on small screens: three sections
    // side by side is the comparison, and breaking them into a column of large
    // cards is what this replaced.
    <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
      <div className="flex min-w-[820px] divide-x divide-slate-200">

        {/* ── Bids ── */}
        <section className="flex-1 min-w-[260px] p-4">
          <p className="text-[12px] font-bold text-slate-500 flex items-center gap-1.5 mb-2">
            <Layers size={13} /> Bids
          </p>
          <p className="text-[26px] leading-none font-extrabold text-slate-900">{num(bids.total)}</p>
          <p className="text-[11px] text-slate-500 mt-1">Total bids</p>
          <p className="text-[12px] text-slate-600 mt-1.5">Total value: <span className="font-bold text-slate-800">{money(bids.value)}</span></p>

          <div className="mt-3 space-y-1">
            {STATUS_ROWS.map((row) => {
              const b = bids[row.key] as Bucket;
              return (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => onOpenStatus?.(row.status)}
                  disabled={!onOpenStatus}
                  className="w-full flex items-center gap-2 text-left px-1.5 py-1 rounded-lg enabled:hover:bg-slate-50 disabled:cursor-default"
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.dot}`} />
                  <span className="text-[12px] text-slate-700 flex-1 truncate">{row.label}</span>
                  <span className="text-[12px] font-bold text-slate-900 tabular-nums">{num(b.count)}</span>
                  <span className="text-[11px] text-slate-500 tabular-nums w-16 text-right">{money(b.value)}</span>
                </button>
              );
            })}
          </div>

          {/* The four rows above deliberately do not add up to the total — said
              here so the gap reads as a definition rather than a bug. */}
          {(bids.open.count > 0 || bids.voided.count > 0) && (
            <p className="text-[10.5px] text-slate-400 mt-2 leading-snug">
              {bids.open.count > 0 && <>{num(bids.open.count)} still open. </>}
              {bids.voided.count > 0 && <>{num(bids.voided.count)} job(s) deleted on the platform — no outcome possible.</>}
            </p>
          )}
        </section>

        {/* ── Connects ── */}
        <section className="flex-1 min-w-[250px] p-4">
          <p className="text-[12px] font-bold text-slate-500 flex items-center gap-1.5 mb-2">
            <Database size={13} /> Connects
          </p>
          <p className="text-[26px] leading-none font-extrabold text-slate-900">{num(connects.used)}</p>
          <p className="text-[11px] text-slate-500 mt-1">
            {connects.allowance ? <>Connects used / {num(connects.allowance)}</> : "Connects used"}
          </p>

          {/* No allowance configured means no ceiling to measure against, so no
              bar — rather than a bar against an invented maximum. */}
          {connects.usedPct !== null ? (
            <div className="mt-2">
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, connects.usedPct)}%` }} />
              </div>
              <p className="text-[10.5px] text-slate-500 mt-1">
                {connects.usedPct}% used · {num(connects.remaining || 0)} left
              </p>
            </div>
          ) : (
            <p className="text-[10.5px] text-slate-400 mt-2">No allowance configured, so usage cannot be shown as a share.</p>
          )}

          <p className="text-[12px] text-slate-600 mt-2">
            <span className="font-bold text-slate-800">{money(connects.cost)}</span> total cost
          </p>

          <div className="mt-3 space-y-2">
            {connects.classified > 0 ? (
              <>
                {[
                  { label: "Boosted", n: connects.boosted, pct: connects.boostedPct, bar: "bg-violet-500" },
                  { label: "Organic", n: connects.organic, pct: connects.organicPct, bar: "bg-emerald-500" },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-center justify-between text-[11.5px]">
                      <span className="text-slate-700">{row.label}</span>
                      <span className="font-bold text-slate-900 tabular-nums">{num(row.n)}</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-0.5">
                      <div className={`h-full rounded-full ${row.bar}`} style={{ width: `${row.pct ?? 0}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-[10.5px] text-slate-400">Share of classified bids.</p>
              </>
            ) : (
              <p className="text-[11px] text-slate-400">No bids classified as boosted or organic yet.</p>
            )}
          </div>
        </section>

        {/* ── Conversion ── */}
        <section className="flex-1 min-w-[280px] p-4">
          <p className="text-[12px] font-bold text-slate-500 flex items-center gap-1.5 mb-2">
            <TrendingUp size={13} /> Conversion
          </p>
          <div className="space-y-2">
            {RINGS.map((ring) => {
              const c: Conversion = conversion[ring.key] || { rate: null, numerator: 0, denominator: 0 };
              const title = c.rate === null && c.unavailableReason ? c.unavailableReason : ring.tip;
              return (
                <div key={ring.key} className="flex items-center gap-3" title={title}>
                  <Ring pct={c.rate} colour={ring.colour} />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-bold text-slate-800 leading-tight">{ring.label}</p>
                    <p className="text-[11px] text-slate-500 tabular-nums">
                      {c.denominator > 0
                        ? `${num(c.numerator)} / ${num(c.denominator)} bids`
                        : "nothing to measure yet"}
                    </p>
                    {c.rate === null && c.unavailableReason && (
                      <p className="text-[10px] text-amber-700 leading-snug mt-0.5">{c.unavailableReason}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BidStatsPanel;
